import Dexie, { Table } from 'dexie';
import Fuse, { IFuseOptions, FuseResult } from 'fuse.js';
import type { CreditPrices, CreditSalePriceRule } from '@/app/lib/creditPricing';

// Enriched item with all nested data
export interface EnrichedItem {
  id: number;
  organization_id: number;
  name: string;
  sku?: string;
  barcode?: string;
  unit_id?: number;
  hsn_code?: string;
  item_type?: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  rate_decimal_places?: number; // Number of decimal places for rate (0-4)
  
  // Nested pricing data
  prices?: {
    sale_price?: number;
    purchase_price?: number;
    mrp?: number;
    is_tax_inclusive?: boolean; // Flag to indicate if prices include tax
    // Site-specific prices
    site_prices?: {
      site_id: number;
      sale_price?: number;
      purchase_price?: number;
      is_tax_inclusive?: boolean; // Site-specific tax-inclusive flag
    }[];
  };
  
  // Nested tax data
  tax_rate?: {
    id: number;
    name: string;
    code: string;
    rate: number;
    description?: string;
    components: {
      id: number;
      name: string;
      code: string;
      rate: number;
    }[];
  };
  
  // Any additional data needed for billing
  stock?: {
    quantity: number;
    min_stock?: number;
    max_stock?: number;
  };
  
  // Sale price rules for quantity-based pricing
  sale_price_rules?: {
    id: number;
    min_qty: string | number;
    max_qty?: string | number | null;
    rule_type: string;
    value: string | number;
    is_percentage: boolean;
    is_active: boolean;
    name?: string;
    priority?: number;
    start_date?: string | null;
    end_date?: string | null;
    conditions?: any;
    site_id?: number | null;
  }[];

  // Credit pricing (sync with include_credit=true, org switch on). Used only for
  // credit parties; prices / sale_price_rules above stay standard-only.
  credit_prices?: CreditPrices | null;
  credit_sale_price_rules?: CreditSalePriceRule[] | null;
}

export interface SyncMetadata {
  id?: number;
  last_sync: Date;
  status: 'success' | 'error' | 'pending';
  item_count: number;
  error?: string;
}

// Account-related interfaces for IndexedDB storage
export interface LocalAccountType {
  id: number;
  name: string;
  description: string | null;
  nature: 'debit' | 'credit';
  created_at: string;
  updated_at: string;
}

export interface LocalAccountGroup {
  id: number;
  organization_id: number;
  name: string;
  code: string | null;
  account_type_id: number;
  parent_id: number | null;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Denormalized for faster queries
  account_type_name?: string;
  parent_name?: string;
}

export interface LocalAccount {
  id: number;
  organization_id: number;
  name: string;
  code: string | null;
  account_group_id: number;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Denormalized for faster queries
  account_group_name?: string;
  account_type_id?: number;
  account_type_name?: string;
}

class MickroDatabase extends Dexie {
  items!: Table<EnrichedItem>;
  syncMetadata!: Table<SyncMetadata>;
  accountTypes!: Table<LocalAccountType>;
  accountGroups!: Table<LocalAccountGroup>;
  accounts!: Table<LocalAccount>;

  constructor() {
    super('MickroDatabase');

    this.version(1).stores({
      // Index by id, organization_id, and searchable fields
      items: 'id, organization_id, name, sku, barcode, [organization_id+name], [organization_id+sku], [organization_id+barcode]',
      syncMetadata: '++id'
    });

    // Version 2: Add account-related tables
    this.version(2).stores({
      items: 'id, organization_id, name, sku, barcode, [organization_id+name], [organization_id+sku], [organization_id+barcode]',
      syncMetadata: '++id',
      accountTypes: 'id, name, nature',
      accountGroups: 'id, organization_id, name, account_type_id, parent_id, [organization_id+name], [organization_id+account_type_id]',
      accounts: 'id, organization_id, name, code, account_group_id, [organization_id+name], [organization_id+account_group_id]'
    });
  }

  async clearAllData(): Promise<void> {
    await this.transaction('rw', this.items, this.syncMetadata, this.accountTypes, this.accountGroups, this.accounts, async () => {
      await Promise.all([
        this.items.clear(),
        this.syncMetadata.clear(),
        this.accountTypes.clear(),
        this.accountGroups.clear(),
        this.accounts.clear()
      ]);
    });
  }
}

let db: MickroDatabase | null = null;

export async function initializeDatabase(): Promise<MickroDatabase> {
  if (db) {
    return db;
  }

  try {
    console.log('[IndexedDB] Initializing database...');
    db = new MickroDatabase();
    
    // Test database connection
    await db.open();
    console.log('[IndexedDB] Database initialized successfully');
    
    return db;
  } catch (error) {
    console.error('[IndexedDB] Failed to initialize database:', error);
    throw error;
  }
}

export function getDatabase(): MickroDatabase {
  if (!db) {
    throw new Error('Database not initialized. Call initializeDatabase() first.');
  }
  return db;
}

export async function closeDatabase(): Promise<void> {
  if (db) {
    db.close();
    db = null;
  }
}

// Search items with all nested data using fuzzy search
export async function searchItemsAsync(query: string, organizationId: number, limit: number = 50): Promise<EnrichedItem[]> {
  const database = getDatabase();
  
  console.log(`[searchItemsAsync] Searching for "${query}" in org ${organizationId}`);
  
  // Get all items for the organization
  const allOrgItems = await database.items
    .where('organization_id')
    .equals(organizationId)
    .toArray();
  
  // Filter only active items
  const orgItems = allOrgItems.filter(item => item.is_active === true);
  
  console.log(`[searchItemsAsync] Found ${allOrgItems.length} total items, ${orgItems.length} active items for org ${organizationId}`);
  
  if (orgItems.length === 0) {
    return [];
  }
  
  // Create a more intelligent search approach
  // First, try exact matches for quick results
  const lowerQuery = query.toLowerCase();
  const exactMatches = orgItems.filter(item => {
    const name = item.name?.toLowerCase() || '';
    const sku = item.sku?.toLowerCase() || '';
    const barcode = item.barcode?.toLowerCase() || '';
    return name.includes(lowerQuery) || sku.includes(lowerQuery) || barcode.includes(lowerQuery);
  });
  
  if (exactMatches.length >= limit) {
    console.log(`[searchItemsAsync] Found ${exactMatches.length} exact matches`);
    return exactMatches.slice(0, limit);
  }
  
  // Configure Fuse.js options for fuzzy search
  const fuseOptions: IFuseOptions<EnrichedItem> = {
    keys: [
      { name: 'name', weight: 0.7 },
      { name: 'sku', weight: 0.2 },
      { name: 'barcode', weight: 0.1 },
      { name: 'description', weight: 0.1 }
    ],
    threshold: 0.6, // Adjusted for better fuzzy matching
    includeScore: true,
    shouldSort: true,
    minMatchCharLength: 1,
    findAllMatches: true,
    ignoreLocation: true, // Don't care about position in string
    // Extended search to support special patterns
    useExtendedSearch: true
  };
  
  // Create Fuse instance
  const fuse = new Fuse(orgItems, fuseOptions);
  
  // Handle different search patterns
  let searchResults: FuseResult<EnrichedItem>[] = [];
  
  // Check if query looks like an abbreviation (e.g., "S C P" or "SCP")
  const isAbbreviation = /^[A-Za-z](\s*[A-Za-z])*$/.test(query) && query.replace(/\s/g, '').length <= 5;
  
  if (isAbbreviation) {
    // For abbreviations, search for items where words start with those letters
    const letters = query.toUpperCase().split(/\s*/).filter(l => l);
    
    // Custom filter for abbreviation matching
    const abbreviationMatches = orgItems.filter(item => {
      const name = item.name || '';
      const words = name.toUpperCase().split(/\s+/);
      
      // Check if first letters of words match the abbreviation
      if (words.length >= letters.length) {
        const firstLetters = words.slice(0, letters.length).map(w => w[0]);
        return firstLetters.join('') === letters.join('');
      }
      return false;
    });
    
    if (abbreviationMatches.length > 0) {
      console.log(`[searchItemsAsync] Found ${abbreviationMatches.length} abbreviation matches`);
      return abbreviationMatches.slice(0, limit);
    }
  }
  
  // For multi-word queries like "Sak Chi" or "Sak pow"
  if (query.includes(' ')) {
    const words = query.split(/\s+/).filter(w => w);
    
    // Try to match each word at the beginning of words in the item name
    const multiWordMatches = orgItems.filter(item => {
      const itemWords = (item.name || '').toLowerCase().split(/\s+/);
      
      return words.every(searchWord => {
        const searchLower = searchWord.toLowerCase();
        // Check if any word in the item starts with this search word
        return itemWords.some(itemWord => itemWord.startsWith(searchLower));
      });
    });
    
    if (multiWordMatches.length > 0) {
      console.log(`[searchItemsAsync] Found ${multiWordMatches.length} multi-word matches`);
      return multiWordMatches.slice(0, limit);
    }
    
    // If no direct matches, use Fuse.js with AND operator for all words
    const andPattern = words.map(word => `'${word}`).join(' ');
    searchResults = fuse.search(andPattern, { limit });
  } else {
    // Single word search - use standard fuzzy search
    searchResults = fuse.search(query, { limit });
  }
  
  // Extract items from results
  const fuzzyMatches = searchResults.map(result => {
    console.log(`[searchItemsAsync] Fuzzy match: ${result.item.name} (score: ${result.score})`);
    return result.item;
  });
  
  // Combine exact matches (if any) with fuzzy matches, avoiding duplicates
  const combinedResults: EnrichedItem[] = [...exactMatches];
  const seenIds = new Set(exactMatches.map(item => item.id));
  
  for (const fuzzyMatch of fuzzyMatches) {
    if (!seenIds.has(fuzzyMatch.id)) {
      combinedResults.push(fuzzyMatch);
      seenIds.add(fuzzyMatch.id);
    }
    if (combinedResults.length >= limit) break;
  }
  
  console.log(`[searchItemsAsync] Found ${combinedResults.length} total items matching "${query}"`);
  return combinedResults.slice(0, limit);
}

export async function getItemCountAsync(organizationId: number): Promise<number> {
  const database = getDatabase();
  console.log(`[getItemCountAsync] Counting items for org ${organizationId}`);
  
  const count = await database.items
    .where('organization_id')
    .equals(organizationId)
    .count();
    
  console.log(`[getItemCountAsync] Found ${count} items for org ${organizationId}`);
  
  // Debug: Check all items
  const allItems = await database.items.toArray();
  console.log(`[getItemCountAsync] Total items in DB: ${allItems.length}, orgs: ${allItems.map(i => i.organization_id).join(', ')}`);
  
  return count;
}

// Sync enriched items from backend
export async function syncItemsAsync(items: EnrichedItem[], organizationId: number): Promise<void> {
  const database = getDatabase();
  
  await database.transaction('rw', database.items, database.syncMetadata, async () => {
    // Clear existing items for this organization
    await database.items
      .where('organization_id')
      .equals(organizationId)
      .delete();
    
    // Add new enriched items
    await database.items.bulkAdd(items);
    
    // Update sync metadata
    await database.syncMetadata.clear();
    await database.syncMetadata.add({
      last_sync: new Date(),
      status: 'success',
      item_count: items.length
    });
  });
  
  console.log(`[IndexedDB] Synced ${items.length} enriched items for organization ${organizationId}`);
}

// Get item by ID with all nested data
export async function getItemByIdAsync(itemId: number): Promise<EnrichedItem | undefined> {
  const database = getDatabase();
  return await database.items.get(itemId);
}

// Get item price based on site and price type
export async function getItemPriceAsync(itemId: number, priceType: 'sale' | 'purchase' | 'mrp', siteId?: number | null): Promise<number | null> {
  const database = getDatabase();
  const item = await database.items.get(itemId);
  
  if (!item || !item.prices) {
    return null;
  }
  
  // Check for site-specific price first
  if (siteId && item.prices.site_prices) {
    const sitePrice = item.prices.site_prices.find(sp => sp.site_id === siteId);
    if (sitePrice) {
      if (priceType === 'sale' && sitePrice.sale_price !== undefined) {
        return sitePrice.sale_price;
      }
      if (priceType === 'purchase' && sitePrice.purchase_price !== undefined) {
        return sitePrice.purchase_price;
      }
    }
  }
  
  // Fall back to general prices
  switch (priceType) {
    case 'sale':
      return item.prices.sale_price ?? null;
    case 'purchase':
      return item.prices.purchase_price ?? null;
    case 'mrp':
      return item.prices.mrp ?? null;
    default:
      return null;
  }
}

// Get item price details including tax-inclusive flag
export async function getItemPriceDetailsAsync(
  itemId: number, 
  priceType: 'sale' | 'purchase' | 'mrp', 
  siteId?: number | null
): Promise<{ price: number | null; is_tax_inclusive: boolean } | null> {
  const database = getDatabase();
  const item = await database.items.get(itemId);
  
  if (!item || !item.prices) {
    return null;
  }
  
  // Check for site-specific price first
  if (siteId && item.prices.site_prices) {
    const sitePrice = item.prices.site_prices.find(sp => sp.site_id === siteId);
    if (sitePrice) {
      if (priceType === 'sale' && sitePrice.sale_price !== undefined) {
        return {
          price: sitePrice.sale_price,
          is_tax_inclusive: sitePrice.is_tax_inclusive ?? false
        };
      }
      if (priceType === 'purchase' && sitePrice.purchase_price !== undefined) {
        return {
          price: sitePrice.purchase_price,
          is_tax_inclusive: sitePrice.is_tax_inclusive ?? false
        };
      }
    }
  }
  
  // Fall back to general prices
  let price: number | null = null;
  switch (priceType) {
    case 'sale':
      price = item.prices.sale_price ?? null;
      break;
    case 'purchase':
      price = item.prices.purchase_price ?? null;
      break;
    case 'mrp':
      price = item.prices.mrp ?? null;
      break;
  }
  
  if (price !== null) {
    console.log(`[getItemPriceDetailsAsync] Item ${itemId} price details:`, {
      price,
      is_tax_inclusive: item.prices.is_tax_inclusive ?? false,
      item_name: item.name
    });
    return {
      price,
      is_tax_inclusive: item.prices.is_tax_inclusive ?? false
    };
  }
  
  return null;
}

// Get tax rate for an item with components
export async function getTaxRateAsync(itemId: number): Promise<EnrichedItem['tax_rate'] | null> {
  const database = getDatabase();
  const item = await database.items.get(itemId);
  
  return item?.tax_rate ?? null;
}

// Get sync status
export async function getSyncStatusAsync(): Promise<SyncMetadata | undefined> {
  const database = getDatabase();
  const metadata = await database.syncMetadata.toArray();
  return metadata[0]; // Return the most recent sync metadata
}

// Check if database is initialized
export function isDatabaseInitialized(): boolean {
  return db !== null;
}

// Batch get items by IDs
export async function getItemsByIdsAsync(itemIds: number[]): Promise<EnrichedItem[]> {
  const database = getDatabase();
  return await database.items
    .where('id')
    .anyOf(itemIds)
    .toArray();
}

// Get all items for an organization (for offline mode)
export async function getAllItemsAsync(organizationId: number): Promise<EnrichedItem[]> {
  const database = getDatabase();
  return await database.items
    .where('organization_id')
    .equals(organizationId)
    .toArray();
}

// Calculate best price for an item based on quantity and rules
export async function calculateItemPriceAsync(
  itemId: number, 
  quantity: number, 
  priceType: 'sale' | 'purchase',
  siteId?: number | null
): Promise<{
  price: number;
  source: 'base' | 'site' | 'rule';
  ruleId?: number;
} | null> {
  const database = getDatabase();
  const item = await database.items.get(itemId);
  
  console.log(`[calculateItemPrice] Calculating price for item ${itemId}, qty=${quantity}, type=${priceType}, siteId=${siteId}`);
  
  if (!item || !item.prices) {
    console.log(`[calculateItemPrice] No item or prices found`);
    return null;
  }
  
  console.log(`[calculateItemPrice] Item prices:`, item.prices);
  console.log(`[calculateItemPrice] Sale price rules:`, item.sale_price_rules);
  
  // For purchase price, return base or site-specific price
  if (priceType === 'purchase') {
    if (siteId && item.prices.site_prices) {
      const sitePrice = item.prices.site_prices.find(sp => sp.site_id === siteId);
      if (sitePrice?.purchase_price !== undefined) {
        return { price: sitePrice.purchase_price, source: 'site' };
      }
    }
    return item.prices.purchase_price ? 
      { price: item.prices.purchase_price, source: 'base' } : null;
  }
  
  // For sale price, check rules first
  let bestPrice = 0;
  let source: 'base' | 'site' | 'rule' = 'base';
  let ruleId: number | undefined;
  
  // Check site-specific base price first
  if (siteId && item.prices.site_prices) {
    const sitePrice = item.prices.site_prices.find(sp => sp.site_id === siteId);
    if (sitePrice?.sale_price !== undefined && sitePrice.sale_price !== null) {
      bestPrice = typeof sitePrice.sale_price === 'string' ? parseFloat(sitePrice.sale_price) : sitePrice.sale_price;
      source = 'site';
    }
  }
  
  // If no site price, use base sale price
  if (bestPrice === 0 && item.prices.sale_price) {
    bestPrice = typeof item.prices.sale_price === 'string' ? parseFloat(item.prices.sale_price) : item.prices.sale_price;
  }
  
  // Check sale price rules
  if (item.sale_price_rules && item.sale_price_rules.length > 0) {
    const now = new Date().toISOString().split('T')[0];
    
    // Sort rules by priority (lower number = higher priority)
    const sortedRules = [...item.sale_price_rules].sort((a, b) => (a.priority || 999) - (b.priority || 999));
    
    for (const rule of sortedRules) {
      // Check if rule is applicable
      if (!rule.is_active) continue;
      
      // Get min/max qty from either direct properties or conditions object
      let minQty, maxQty;
      if (rule.conditions && typeof rule.conditions === 'object') {
        minQty = rule.conditions.min_qty ? 
          (typeof rule.conditions.min_qty === 'string' ? parseFloat(rule.conditions.min_qty) : rule.conditions.min_qty) : 
          (typeof rule.min_qty === 'string' ? parseFloat(rule.min_qty) : rule.min_qty);
        maxQty = rule.conditions.max_qty !== undefined ? 
          (rule.conditions.max_qty === null ? null : 
            (typeof rule.conditions.max_qty === 'string' ? parseFloat(rule.conditions.max_qty) : rule.conditions.max_qty)) :
          (rule.max_qty ? (typeof rule.max_qty === 'string' ? parseFloat(rule.max_qty) : rule.max_qty) : null);
      } else {
        minQty = typeof rule.min_qty === 'string' ? parseFloat(rule.min_qty) : rule.min_qty;
        maxQty = rule.max_qty ? (typeof rule.max_qty === 'string' ? parseFloat(rule.max_qty) : rule.max_qty) : null;
      }
      
      console.log(`[calculateItemPrice] Checking rule ${rule.id}: qty ${quantity} vs min ${minQty} max ${maxQty}`);
      
      if (quantity < minQty) continue;
      if (maxQty !== null && quantity > maxQty) continue;
      if (rule.start_date && rule.start_date > now) continue;
      if (rule.end_date && rule.end_date < now) continue;
      
      let rulePrice = 0;
      const value = typeof rule.value === 'string' ? parseFloat(rule.value) : rule.value;
      
      // Get base price for calculation
      let basePrice = bestPrice;
      
      // For profit-based rules, we need the purchase price
      // First check site-specific purchase price, then fall back to base purchase price
      let purchasePrice = null;
      if (siteId && item.prices.site_prices) {
        const sitePrices = item.prices.site_prices.find(sp => sp.site_id === siteId);
        purchasePrice = sitePrices?.purchase_price || item.prices.purchase_price;
      } else {
        purchasePrice = item.prices.purchase_price;
      }
      
      console.log(`[calculateItemPrice] Purchase price for calculations: ${purchasePrice}`);
      
      switch (rule.rule_type) {
        case 'fixed_sale_price':
          rulePrice = value;
          break;
          
        case 'profit_percentage':
          // If we have a purchase price, calculate based on profit margin
          if (purchasePrice) {
            const cost = typeof purchasePrice === 'string' ? parseFloat(purchasePrice) : purchasePrice;
            rulePrice = cost * (1 + value / 100);
            console.log(`[calculateItemPrice] Profit percentage rule: cost=${cost}, value=${value}%, result=${rulePrice}`);
          } else {
            console.log(`[calculateItemPrice] No purchase price for profit percentage calculation`);
            // If no purchase price, can't calculate profit-based price
            continue;
          }
          break;
          
        case 'fixed_profit':
          // Add fixed profit to purchase price
          if (purchasePrice) {
            const cost = typeof purchasePrice === 'string' ? parseFloat(purchasePrice) : purchasePrice;
            rulePrice = cost + value;
          }
          break;
          
        case 'discount_percentage':
          // Apply discount to base price
          rulePrice = basePrice * (1 - value / 100);
          break;
          
        case 'discount_amount':
          // Subtract fixed discount from base price
          rulePrice = basePrice - value;
          break;
      }
      
      // Apply the rule (don't compare prices, just apply the first matching rule by priority)
      if (rulePrice > 0) {
        console.log(`[calculateItemPrice] Applying rule ${rule.id} (${rule.rule_type}): price ${rulePrice}`);
        bestPrice = rulePrice;
        source = 'rule';
        ruleId = rule.id;
        break; // Use first matching rule by priority
      } else {
        console.log(`[calculateItemPrice] Rule ${rule.id} didn't produce a valid price`);
      }
    }
  }
  
  console.log(`[calculateItemPrice] Final price: ${bestPrice}, source: ${source}, ruleId: ${ruleId}`);
  return { price: bestPrice, source, ruleId };
}

// ============================================
// Account-related functions
// ============================================

// Sync account types (these are global, not org-specific)
export async function syncAccountTypesAsync(accountTypes: LocalAccountType[]): Promise<void> {
  const database = getDatabase();

  await database.transaction('rw', database.accountTypes, async () => {
    await database.accountTypes.clear();
    await database.accountTypes.bulkAdd(accountTypes);
  });

  console.log(`[IndexedDB] Synced ${accountTypes.length} account types`);
}

// Sync account groups for an organization
export async function syncAccountGroupsAsync(accountGroups: LocalAccountGroup[], organizationId: number): Promise<void> {
  const database = getDatabase();

  await database.transaction('rw', database.accountGroups, async () => {
    // Clear existing groups for this organization
    await database.accountGroups
      .where('organization_id')
      .equals(organizationId)
      .delete();

    // Add new groups
    await database.accountGroups.bulkAdd(accountGroups);
  });

  console.log(`[IndexedDB] Synced ${accountGroups.length} account groups for organization ${organizationId}`);
}

// Sync accounts for an organization
export async function syncAccountsAsync(accounts: LocalAccount[], organizationId: number): Promise<void> {
  const database = getDatabase();

  await database.transaction('rw', database.accounts, async () => {
    // Clear existing accounts for this organization
    await database.accounts
      .where('organization_id')
      .equals(organizationId)
      .delete();

    // Add new accounts
    await database.accounts.bulkAdd(accounts);
  });

  console.log(`[IndexedDB] Synced ${accounts.length} accounts for organization ${organizationId}`);
}

// Get all account types
export async function getAllAccountTypesAsync(): Promise<LocalAccountType[]> {
  const database = getDatabase();
  return await database.accountTypes.toArray();
}

// Get all account groups for an organization
export async function getAllAccountGroupsAsync(organizationId: number): Promise<LocalAccountGroup[]> {
  const database = getDatabase();
  return await database.accountGroups
    .where('organization_id')
    .equals(organizationId)
    .toArray();
}

// Get all accounts for an organization
export async function getAllAccountsAsync(organizationId: number): Promise<LocalAccount[]> {
  const database = getDatabase();
  return await database.accounts
    .where('organization_id')
    .equals(organizationId)
    .toArray();
}

// Get accounts by group ID
export async function getAccountsByGroupIdAsync(organizationId: number, groupId: number): Promise<LocalAccount[]> {
  const database = getDatabase();
  return await database.accounts
    .where('[organization_id+account_group_id]')
    .equals([organizationId, groupId])
    .toArray();
}

// Search accounts by name or code
export async function searchAccountsAsync(query: string, organizationId: number, limit: number = 50): Promise<LocalAccount[]> {
  const database = getDatabase();

  console.log(`[searchAccountsAsync] Searching for "${query}" in org ${organizationId}`);

  // Get all accounts for the organization
  const allOrgAccounts = await database.accounts
    .where('organization_id')
    .equals(organizationId)
    .toArray();

  // Filter only active accounts
  const orgAccounts = allOrgAccounts.filter(account => account.is_active === true);

  console.log(`[searchAccountsAsync] Found ${allOrgAccounts.length} total accounts, ${orgAccounts.length} active accounts`);

  if (orgAccounts.length === 0) {
    return [];
  }

  const lowerQuery = query.toLowerCase();

  // Search by name or code
  const matches = orgAccounts.filter(account => {
    const name = account.name?.toLowerCase() || '';
    const code = account.code?.toLowerCase() || '';
    return name.includes(lowerQuery) || code.includes(lowerQuery);
  });

  // Sort by name
  matches.sort((a, b) => a.name.localeCompare(b.name));

  console.log(`[searchAccountsAsync] Found ${matches.length} matches for "${query}"`);
  return matches.slice(0, limit);
}

// Get account by ID
export async function getAccountByIdAsync(accountId: number): Promise<LocalAccount | undefined> {
  const database = getDatabase();
  return await database.accounts.get(accountId);
}

// Get account group by ID
export async function getAccountGroupByIdAsync(groupId: number): Promise<LocalAccountGroup | undefined> {
  const database = getDatabase();
  return await database.accountGroups.get(groupId);
}

// Get account type by ID
export async function getAccountTypeByIdAsync(typeId: number): Promise<LocalAccountType | undefined> {
  const database = getDatabase();
  return await database.accountTypes.get(typeId);
}

// Get account counts for sync status
export async function getAccountCountAsync(organizationId: number): Promise<number> {
  const database = getDatabase();
  return await database.accounts
    .where('organization_id')
    .equals(organizationId)
    .count();
}

// Check if accounts are synced for an organization
export async function hasAccountsAsync(organizationId: number): Promise<boolean> {
  const count = await getAccountCountAsync(organizationId);
  return count > 0;
}